// src/components/desk/AdvertisingHistory.jsx — "how has this property been advertised?"
// ----------------------------------------------------------------------------
// Owner request 2026-08-01: "so agent always know their pricing history too, how they have
// ever shared it, how they have advertised."
//
// ── WHY THIS NEEDED NO NEW TABLE AND NO SERVER DEPLOY ───────────────────────
// The record already exists and is append-only. `register_reshare` (0057) writes a
// `listing.reshare_registered` event per repost carrying `data.raw_inputs.price/currency` —
// the RAW caller inputs, nulls included — and the extension fills those from the POSTED
// SNAPSHOT, i.e. the number actually typed into the destination portal's form. Until now
// nothing read it back: the only consumer of `raw_inputs` was a one-shot intent backfill
// inside 0057 itself. This panel is that missing reader.
//
// ⚠️ It deliberately does NOT read `listing.price` as "the advertised price". That column is
// overwritten by every later ingest (`upsert_listing ... on conflict do update`), so it is
// the CURRENT price, not the historical one. Showing it as history would quietly restate
// today's number as though it were what the agent posted months ago — the exact class of
// "silently wrong derived field" this codebase keeps getting bitten by. The event is the
// only immutable record of what was published, so the event is what this reads.
//
// ⚠️ `raw_inputs.price` is NULL whenever the repost carried no posted snapshot (the portal
// form was never read). The event says so itself via `price_inherited_from_origin`, and that
// case is rendered as "not captured", never as a price. An absent observation must not be
// dressed up as an observed one.
//
// Fetched LAZILY, on first expand. ShareTab's whole payload-cache design exists to make
// re-opening instant; adding an unconditional second request to every open would spend
// exactly what that bought, for a panel most opens never look at.

import { useState } from 'react'
import { t } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { card } from './primitives.jsx'
import { getPropertyEvents } from '../../db/core.js'

const PORTAL_LABEL = { korter: 'Korter', ss: 'SS.ge', myhome: 'MyHome', sentavus: 'senTavus' }
// core stores the origin portal as "ss.ge"/"myhome.ge" while a live scrape says "ss"/"myhome"
// (the same split that broke image conversion earlier today) — match on the stem.
const portalOf = src => {
  const s = String(src || '').toLowerCase()
  const hit = Object.keys(PORTAL_LABEL).find(k => s.startsWith(k))
  return hit ? PORTAL_LABEL[hit] : (src || '—')
}

const fmtDate = iso => {
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) }
  catch { return iso || '' }
}
const fmtMoney = (n, cur) =>
  n == null ? null : `${Number(n).toLocaleString('en-US')} ${cur || ''}`.trim()

export function AdvertisingHistory({ propertyId }) {
  const [open, setOpen]   = useState(false)
  const [rows, setRows]   = useState(null)     // null = never loaded
  const [loading, setLoad] = useState(false)
  const [err, setErr]     = useState(null)

  async function toggle() {
    const next = !open
    setOpen(next)
    if (!next || rows || loading || !propertyId) return
    setLoad(true); setErr(null)
    try {
      // 200 events covers a busy property's whole life; reposts are a small slice of the
      // stream, and this endpoint caps at 500 server-side anyway.
      const d = await getPropertyEvents(propertyId, { limit: 200 })
      const listings = d?.listings || {}
      const out = (d?.events || [])
        .filter(e => e.verb === 'listing.reshare_registered' && e.subject_type === 'listing')
        .map(e => {
          const l  = listings[e.subject_id] || {}
          const ri = e.data?.raw_inputs || {}
          // The published price is the event's, or explicitly absent — never the listing's.
          const captured = ri.price != null && !e.data?.price_inherited_from_origin
          return {
            key: e.id,
            at: e.occurred_at,
            portal: portalOf(l.source),
            who: l.author_name || null,
            captured,
            published: captured ? fmtMoney(ri.price, ri.currency || l.currency) : null,
            current: fmtMoney(l.price, l.currency),
            // Only meaningful when we actually know what was published.
            moved: captured && l.price != null && Number(l.price) !== Number(ri.price),
            status: l.status || null,
          }
        })
      setRows(out)
    } catch (e) { setErr(e.message) } finally { setLoad(false) }
  }

  if (!propertyId) return null

  return (
    <div className={card}>
      <button type="button" onClick={toggle}
        className="w-full flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>{t('desk_adhist_title')}</span>
        <span className="text-muted-foreground">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="mt-2">
          {loading && <p className="text-sm text-muted-foreground">{t('desk_loading')}</p>}
          {err && <p className="text-sm text-destructive">{err}</p>}
          {rows && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('desk_adhist_none')}</p>
          )}
          {rows && rows.length > 0 && (
            <>
              <div className="space-y-1.5">
                {rows.map(r => (
                  <div key={r.key} className="flex items-baseline gap-2 text-sm flex-wrap">
                    <span className="text-muted-foreground tabular-nums shrink-0">{fmtDate(r.at)}</span>
                    <span className="font-medium shrink-0">{r.portal}</span>
                    {r.captured ? (
                      <span className="tabular-nums">{r.published}</span>
                    ) : (
                      // Never show a number here — see the ⚠️ on raw_inputs above.
                      <span className="text-muted-foreground italic">{t('desk_adhist_not_captured')}</span>
                    )}
                    {r.moved && (
                      <span className="text-xs text-amber-600 dark:text-amber-400">
                        {t('desk_adhist_now').replace('{p}', r.current)}
                      </span>
                    )}
                    {r.who && <span className="text-xs text-muted-foreground">· {r.who}</span>}
                  </div>
                ))}
              </div>
              {/* Say what this list is counted from, so the absence of a repost is readable
                  as "not recorded" rather than "did not happen". */}
              <p className="text-xs text-muted-foreground mt-2">
                <Icon name="info" /> {t('desk_adhist_note')}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}
